import { useState, useRef, useEffect, useId } from "react";
import { Info } from "lucide-react";

// (i) explanation icon. Desktop: hover (CSS group-hover). Mobile: tap toggles the
// popover; tapping outside or opening another InfoPopover closes it (one at a time).
export function InfoPopover({ text, testId, placement = "top" }) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onOtherOpen = (e) => {
      if (e.detail !== id) setOpen(false);
    };
    window.addEventListener("tw1:info-popover-open", onOtherOpen);
    return () => window.removeEventListener("tw1:info-popover-open", onOtherOpen);
  }, [id]);

  useEffect(() => {
    if (!open) return;
    const onOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onOutside);
    document.addEventListener("touchstart", onOutside);
    return () => {
      document.removeEventListener("mousedown", onOutside);
      document.removeEventListener("touchstart", onOutside);
    };
  }, [open]);

  if (!text) return null;

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) window.dispatchEvent(new CustomEvent("tw1:info-popover-open", { detail: id }));
  };

  return (
    <span ref={ref} className="relative inline-flex group">
      <button
        type="button"
        onClick={toggle}
        aria-label="Meer info"
        data-testid={testId ? `${testId}-trigger` : undefined}
        className="tw-press text-tw-muted hover:text-tw-yellow transition-colors"
      >
        <Info size={12} strokeWidth={2.5} />
      </button>
      <span
        role="tooltip"
        data-testid={testId ? `${testId}-content` : undefined}
        className={`pointer-events-none absolute z-50 ${placement === "bottom" ? "top-full mt-2" : "bottom-full mb-2"} left-0 sm:left-1/2 sm:-translate-x-1/2 w-48 sm:w-56 max-w-[calc(100vw-2rem)] border border-tw-yellow/50 bg-tw-black px-2.5 py-2 text-[11px] leading-relaxed text-white shadow-lg transition-opacity duration-150 ${
          open ? "opacity-100" : "opacity-0 group-hover:opacity-100"
        }`}
      >
        {text}
      </span>
    </span>
  );
}
